import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { MDSButton, MDSButtonVariant, MDSTextField } from "@/mds";
import { useAppStore } from "@/store";
import {
  approveDelegation,
  cancelDelegation,
  deleteVaultCredential,
  getDelegationCapabilities,
  getDelegations,
  getVaultCredentials,
  saveVaultCredential,
  startDelegation,
  notifyDelegation,
} from "./delegation-api";

const ACTIVE = new Set(["queued", "approved", "executing", "awaiting_approval"]);

export const TaskDelegationPanel = observer(
  ({
    taskId,
    pending,
    onComplete,
  }: {
    taskId: string;
    pending: boolean;
    onComplete: () => void;
  }) => {
    const { store } = useAppStore();
    const enabled =
      store.spaceAccountFeatureFlags.getFlagStatus("mem-agent-task-delegation") === true;
    // Keep account-owned query data separated across account switches.
    const accountId = store.account.myAccount.id;
    const capabilities = useQuery({
      queryKey: ["delegation-capabilities", accountId],
      queryFn: getDelegationCapabilities,
      enabled,
    });
    const available =
      enabled && capabilities.data?.enabled === true && capabilities.data.configured;
    const runs = useQuery({
      queryKey: ["task-delegations", accountId, taskId],
      queryFn: () => getDelegations(taskId),
      enabled: available,
      refetchInterval: 3000,
    });
    const vault = useQuery({
      queryKey: ["delegation-vault", accountId],
      queryFn: getVaultCredentials,
      enabled: available,
    });
    const [credentialId, setCredentialId] = useState<string | null>(null);
    const [showVault, setShowVault] = useState(false);
    const [label, setLabel] = useState("");
    const [origin, setOrigin] = useState("");
    const [username, setUsername] = useState("");
    const [chatSentFor, setChatSentFor] = useState<string | null>(null);
    const [answer, setAnswer] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requestId, setRequestId] = useState(() => crypto.randomUUID());
    const run = runs.data?.[0];
    const active = run && ACTIVE.has(run.status);
    const noticeVersion = run ? `${run.id}:${run.updated_at}` : null;
    const chatSent = noticeVersion !== null && chatSentFor === noticeVersion;

    async function act(action: () => Promise<unknown>) {
      setBusy(true);
      setError(null);
      try {
        await action();
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : "Something went wrong.");
      } finally {
        setBusy(false);
        await runs.refetch();
      }
    }

    if (!enabled || capabilities.data?.enabled === false) return null;

    return (
      <Panel aria-label="Task delegation">
        <h3>Let Mem do this</h3>
        {!available ? (
          <p>
            {capabilities.isError
              ? "Could not check delegation availability."
              : "Browser and vault setup is required before delegation can be activated."}
          </p>
        ) : (
          <>
            <p>
              Mem handles routine browser steps and signs in with your selected login. It asks
              when it needs information or permission beyond your task.
            </p>
            {run ? (
              <div aria-live="polite">
                <strong>
                  {run.status === "needs_input"
                    ? "Waiting for your answer"
                    : run.status.replaceAll("_", " ")}
                </strong>
                <p>{run.message}</p>
              </div>
            ) : null}
            {run && ["needs_input", "awaiting_approval", "review_result"].includes(run.status) ? (
              <MDSButton
                isDisabled={busy || chatSent}
                onPress={() =>
                  void act(async () => {
                    await notifyDelegation(run.id);
                    setChatSentFor(noticeVersion);
                  })
                }
              >
                {chatSent ? "Chat delivery requested" : "Send update to chat"}
              </MDSButton>
            ) : null}
            {run?.action ? (
              <ActionCard>
                <strong>
                  {run.action.action.kind === "credential"
                    ? `Use saved ${run.action.action.credential_field}`
                    : run.action.action.kind}
                </strong>
                <p>
                  {run.action.page_title || "Browser"} · {run.action.page_url}
                </p>
                {run.action.target_label ? <p>Target: {run.action.target_label}</p> : null}
                {run.action.action.url ? <p>Open: {run.action.action.url}</p> : null}
                {run.action.action.value ? <p>Enter: {run.action.action.value}</p> : null}
                <MDSButton
                  isDisabled={busy}
                  variant={MDSButtonVariant.Contrast}
                  onPress={() => void act(() => approveDelegation(run.id, run.action?.id ?? ""))}
                >
                  {run.status === "approved" ? "Retry sending approval" : "Approve action"}
                </MDSButton>
              </ActionCard>
            ) : null}
            {run?.status !== "needs_input" &&
              run?.evidence.map((evidence, index) => (
                <p key={index}>
                  {evidence.summary}
                  <br />
                  {evidence.url}
                </p>
              ))}
            {run?.status === "review_result" && pending ? (
              <MDSButton onPress={onComplete}>I verified the result — mark task done</MDSButton>
            ) : null}
            {run?.status === "queued" ? (
              <MDSButton
                isDisabled={busy}
                onPress={() =>
                  void act(async () => {
                    await startDelegation({
                      id: run.id,
                      task_id: run.task_id,
                      credential_id: run.credential_id,
                    });
                    setRequestId(crypto.randomUUID());
                  })
                }
              >
                Retry starting delegation
              </MDSButton>
            ) : null}
            {active ? (
              <MDSButton isDisabled={busy} onPress={() => void act(() => cancelDelegation(run.id))}>
                Stop delegation
              </MDSButton>
            ) : pending ? (
              <>
                <label>
                  Login for this task{" "}
                  <select
                    value={credentialId ?? run?.credential_id ?? ""}
                    onChange={event => setCredentialId(event.target.value)}
                  >
                    <option value="">No saved login</option>
                    {vault.data?.map(credential => (
                      <option key={credential.id} value={credential.id}>
                        {credential.label} — {credential.origin}
                      </option>
                    ))}
                  </select>
                </label>
                {run?.status === "needs_input" ? (
                  <>
                    <label>
                      Your answer
                      <textarea
                        aria-label="Your answer"
                        value={answer}
                        maxLength={12000}
                        rows={5}
                        onChange={event => setAnswer(event.target.value)}
                      />
                    </label>
                    <p>
                      Provide the missing details above. Save passwords using Manage saved logins.
                    </p>
                  </>
                ) : null}
                <MDSButton
                  isDisabled={
                    busy ||
                    runs.isLoading ||
                    runs.isError ||
                    vault.isError ||
                    (run?.status === "needs_input" && !answer.trim())
                  }
                  variant={MDSButtonVariant.Contrast}
                  onPress={() =>
                    void act(async () => {
                      await startDelegation({
                        id: requestId,
                        task_id: taskId,
                        credential_id: (credentialId ?? run?.credential_id) || null,
                        ...(run?.status === "needs_input"
                          ? { continuation_of: run.id, answer: answer.trim() }
                          : {}),
                      });
                      setRequestId(crypto.randomUUID());
                      setAnswer("");
                    })
                  }
                >
                  {run?.status === "needs_input" ? "Send answer and continue" : "Delegate task"}
                </MDSButton>
              </>
            ) : null}
            <MDSButton
              onPress={() => {
                setPassword("");
                setShowVault(!showVault);
              }}
            >
              {showVault ? "Close vault" : "Manage saved logins"}
            </MDSButton>
            {showVault ? (
              <Vault>
                <h4>Saved logins</h4>
                <p>
                  Logins are encrypted and used only on their saved website. Notes do not change
                  them.
                </p>
                {vault.data?.map(credential => (
                  <div key={credential.id}>
                    {credential.label} · {credential.username} · {credential.origin}
                    <MDSButton
                      isDisabled={busy}
                      onPress={() =>
                        void act(async () => {
                          await deleteVaultCredential(credential.id);
                          if (credentialId === credential.id) setCredentialId("");
                          await vault.refetch();
                        })
                      }
                    >
                      Remove {credential.label}
                    </MDSButton>
                  </div>
                ))}
                <MDSTextField
                  aria-label="Login name"
                  placeholder="Login name"
                  value={label}
                  onChange={setLabel}
                />
                <MDSTextField
                  aria-label="Website"
                  placeholder="https://example.com"
                  value={origin}
                  onChange={setOrigin}
                />
                <MDSTextField
                  aria-label="Username"
                  placeholder="Username"
                  value={username}
                  onChange={setUsername}
                />
                <MDSTextField
                  aria-label="Password"
                  placeholder="Password"
                  value={password}
                  onChange={setPassword}
                  inputProps={{
                    type: "password",
                    autoComplete: "new-password",
                  }}
                />
                <MDSButton
                  isDisabled={busy || !label || !origin || !password}
                  onPress={() =>
                    void act(async () => {
                      const saving = saveVaultCredential({
                        label,
                        origin,
                        username,
                        password,
                      });
                      setPassword("");
                      const credential = await saving;
                      setCredentialId(credential.id);
                      setLabel("");
                      setOrigin("");
                      setUsername("");
                      setShowVault(false);
                      await vault.refetch();
                    })
                  }
                >
                  Save login
                </MDSButton>
              </Vault>
            ) : null}
            {runs.isError || vault.isError ? (
              <p role="alert">Could not load delegation data. Refresh to try again.</p>
            ) : null}
            {error ? <p role="alert">{error}</p> : null}
          </>
        )}
      </Panel>
    );
  }
);

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  overflow-wrap: anywhere;
  h3,
  h4,
  p {
    margin: 0;
  }
  textarea {
    display: block;
    width: 100%;
    resize: vertical;
  }
  select {
    max-width: 100%;
  }
`;
const ActionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 8px;
`;
const Vault = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
